import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Tag, Tooltip, Popconfirm  } from 'antd';
import { Link } from 'react-router-dom'

import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useClienti } from '../hooks/useClienti';
import ClientForm from './ClientForm';

const ClientiTab = () => {
  const { clienti, loading, pagination, fetchClienti, createClient, updateClient, deleteClient } = useClienti();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [search, setSearch] = useState('');
  const [tableSorter, setTableSorter] = useState({ field: 'created_at', order: 'descend' });

  // Fetch inițial cu sortare implicită descrescătoare după 'created_at'
  useEffect(() => {
    fetchClienti({
      page: pagination.current,
      pageSize: pagination.pageSize,
      sortBy: tableSorter.field,
      sortOrder: tableSorter.order === 'descend' ? 'desc' : 'asc'
    });
    // eslint-disable-next-line
  }, []);

  const columns = [
    {
      title: 'Denumire',
      dataIndex: 'denumire',
      key: 'denumire',
      render: (text, record) => 
      <Link to={`/dashboard/oferte/client/${record._id}`}>
        {text}
      </Link>,
    },
    {
      title: 'CF',
      dataIndex: 'id_no',
      key: 'id_no',
      render: (text) => text || '-',
    },
    {
      title: 'Cod TVA',
      dataIndex: 'cod_tva',
      key: 'cod_tva',
      render: (text) => text || '-',
    },
    {
      title: 'Adresa Juridică',
      dataIndex: 'adresa_juridica',
      key: 'adresa_juridica',
      render: (text) => text || '-',
      ellipsis: true,
    },
    {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      render: (text) => text ? (
        <a href={text} target="_blank" rel="noopener noreferrer" className="text-blue-600">
          {text}
        </a>
      ) : '-',
    },
    {
      title: 'Data Creării',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString('ro-RO') : '-',
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editează">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingClient(record);
                setModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Ești sigur că vrei să ștergi acest client?"
            onConfirm={() => deleteClient(record._id)}
            okText="Da"
            cancelText="Nu"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (paginationInfo, filters, sorter) => {
    const sortField = sorter.field || tableSorter.field || 'created_at';
    const sortOrder = sorter.order || tableSorter.order || 'descend';

    setTableSorter({ field: sortField, order: sortOrder });

    fetchClienti({
      page: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      search,
      sortBy: sortField,
      sortOrder: sortOrder === 'descend' ? 'desc' : 'asc'
    });
  };

  const handleSearch = () => {
    fetchClienti({
      page: 1,
      pageSize: pagination.pageSize,
      search,
      sortBy: tableSorter.field,
      sortOrder: tableSorter.order === 'descend' ? 'desc' : 'asc'
    });
  };

  const handleFormSubmit = async (values) => {
    let success = false;
    console.log(values);
    console.log(editingClient);
    if (editingClient) {
      success = await updateClient(editingClient._id, values);
    } else {
      success = await createClient(values);
    }

    if (success) {
      setModalVisible(false);
      setEditingClient(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Lista Clienților</h2>
        <Space>
          <input
            type="text"
            placeholder="Caută client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4, width: 320, fontSize: 16 }}
          />
          <Button onClick={handleSearch}>Caută</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingClient(null);
              setModalVisible(true);
            }}
          >
            Adaugă Client
          </Button>
        </Space>
      </div>

      <Table
        columns={columns.map(col =>
          col.dataIndex === 'created_at'
            ? { ...col, defaultSortOrder: 'descend', sorter: true }
            : col
        )}
        dataSource={clienti}
        loading={loading}
        rowKey="_id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} din ${total} clienți`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editingClient ? 'Editează Client' : 'Adaugă Client Nou'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingClient(null);
        }}
        footer={null}
        width={1200}
        destroyOnClose
        className="top-4"
        bodyStyle={{ maxHeight: '80vh', overflow: 'hidden' }}
      >
        <ClientForm
          initialValues={editingClient}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setModalVisible(false);
            setEditingClient(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default ClientiTab; 